import type {
  ApiResponse,
  GasFundingCheckResponse,
  GasFundingResponse,
  GenerateNonceResponse,
} from '@asetquhub/validation';
import { api } from '../lib/axios';

/**
 * Allowance response from backend
 */
export type AllowanceResponse = {
  allowance: string;
  allowanceRaw: string;
  spender: string;
  isApproved: boolean;
  formattedAllowance: string;
  userAddress: string;
  tokenAddress: string;
};

/**
 * Approval info response from backend
 */
export type ApprovalInfoResponse = {
  backendWallet: string;
  tokenAddress: string;
  chainId: number;
  message: string;
};

/**
 * Nonce generation response from backend
 */
export type NonceResponse = GenerateNonceResponse;

/**
 * Check user's token allowance for backend wallet
 */
const checkAllowance = async (): Promise<AllowanceResponse | null> => {
  const response = await api.get<ApiResponse<AllowanceResponse>>('/api/wallet/allowance');
  return response.data.data ?? null;
};

/**
 * Get approval information needed to approve backend wallet
 */
const getApprovalInfo = async (): Promise<ApprovalInfoResponse | null> => {
  const response = await api.get<ApiResponse<ApprovalInfoResponse>>('/api/wallet/approval-info');
  return response.data.data ?? null;
};

/**
 * Check if user needs gas fee funding for approval transaction
 * Includes all security checks: blacklist, already funded, minimum balance
 */
const checkGasFunding = async (): Promise<GasFundingCheckResponse | null> => {
  const response = await api.get<ApiResponse<GasFundingCheckResponse>>(
    '/api/wallet/approval/check-gas'
  );
  return response.data.data ?? null;
};

/**
 * Generate nonce for gas funding signature verification
 * Returns nonce, deadline, and message to sign
 */
const generateNonce = async (): Promise<NonceResponse | null> => {
  const response = await api.post<ApiResponse<NonceResponse>>(
    '/api/wallet/approval/generate-nonce'
  );
  return response.data.data ?? null;
};

/**
 * Request gas fee funding from backend with signature verification
 * Requires signed message from user (SIWE format)
 * Backend will send small amount of native token (BNB) to user wallet for gas
 */
const fundGas = async (params: {
  signature: string;
  nonce: string;
}): Promise<GasFundingResponse | null> => {
  const response = await api.post<ApiResponse<GasFundingResponse>>(
    '/api/wallet/approval/fund-gas',
    params
  );
  return response.data.data ?? null;
};

export const approvalService = {
  checkAllowance,
  getApprovalInfo,
  checkGasFunding,
  generateNonce,
  fundGas,
};
